(() => {
  const SECTION_SELECTOR = '.scroll-media-hero';
  const instances = new WeakMap();

  class ScrollMediaHero {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('.scroll-media-hero__track');
      this.desktopVideo = section.querySelector('.scroll-media-hero__video--desktop');
      this.mobileVideo = section.querySelector('.scroll-media-hero__video--mobile');

      this.mobileBreakpoint = parseInt(section.dataset.mobileBreakpoint || '767', 10);
      this.enableTextFade = section.dataset.enableTextFade === 'true';
      this.reducedMotionMode = section.dataset.reducedMotionMode || 'poster';

      this.mql = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`);
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.activeVideo = null;
      this.updateQueued = false;
      this.rafId = null;
      this.lastTargetTime = -1;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
    }

    init() {
      if (!this.track || (!this.desktopVideo && !this.mobileVideo)) return;

      if (this.enableTextFade) {
        this.section.classList.add('is-text-animated');
      }

      this.syncMode();
      this.bindVideoEvents();
      this.bindEvents();

      if (this.reducedMotionQuery.matches && this.reducedMotionMode === 'poster') {
        this.section.classList.add('is-reduced-motion');
        return;
      }

      this.prepareActiveVideo();
      this.update();
    }

    bindVideoEvents() {
      [this.desktopVideo, this.mobileVideo].forEach((video) => {
        if (!video) return;
        video.addEventListener('loadedmetadata', this.onLoadedMetadata);
      });
    }

    bindEvents() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if (this.mql.addEventListener) {
        this.mql.addEventListener('change', this.onMediaChange);
      } else if (this.mql.addListener) {
        this.mql.addListener(this.onMediaChange);
      }

      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
      } else if (this.reducedMotionQuery.addListener) {
        this.reducedMotionQuery.addListener(this.onReducedMotionChange);
      }
    }

    onLoadedMetadata(event) {
      const video = event.currentTarget;
      video.dataset.ready = 'true';

      if (video === this.activeVideo) {
        this.section.classList.add('is-video-ready');
        this.warmVideo(video);
        this.update();
      }
    }

    warmVideo(video) {
      if (!video) return;

      video.muted = true;
      video.playsInline = true;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            video.pause();
          })
          .catch(() => {});
      }
    }

    onScroll() {
      this.queueUpdate();
    }

    onResize() {
      this.syncMode();
      this.queueUpdate();
    }

    onMediaChange() {
      this.syncMode();
      this.prepareActiveVideo();
      this.queueUpdate();
    }

    onReducedMotionChange() {
      if (this.reducedMotionQuery.matches && this.reducedMotionMode === 'poster') {
        this.section.classList.add('is-reduced-motion');
      } else {
        this.section.classList.remove('is-reduced-motion');
        this.prepareActiveVideo();
        this.queueUpdate();
      }
    }

    syncMode() {
      const isMobile = this.mql.matches;
      this.section.classList.toggle('is-mobile', isMobile);

      const trackHeight = isMobile
        ? getComputedStyle(this.section).getPropertyValue('--smh-mobile-track')
        : getComputedStyle(this.section).getPropertyValue('--smh-desktop-track');

      if (trackHeight) {
        this.section.style.setProperty('--smh-track-height', trackHeight.trim());
      }

      this.activeVideo = isMobile
        ? (this.mobileVideo || this.desktopVideo)
        : (this.desktopVideo || this.mobileVideo);

      this.section.classList.remove('is-video-ready');
      this.lastTargetTime = -1;
    }

    prepareActiveVideo() {
      if (!this.activeVideo) return;

      this.activeVideo.muted = true;
      this.activeVideo.playsInline = true;
      this.activeVideo.pause();

      if (this.activeVideo.dataset.ready === 'true' && this.activeVideo.readyState >= 1) {
        this.section.classList.add('is-video-ready');
        this.warmVideo(this.activeVideo);
      }
    }

    queueUpdate() {
      if (this.updateQueued) return;

      this.updateQueued = true;
      this.rafId = window.requestAnimationFrame(() => {
        this.updateQueued = false;
        this.update();
      });
    }

    getProgress() {
      const rect = this.track.getBoundingClientRect();
      const scrollDistance = this.track.offsetHeight - window.innerHeight;

      if (scrollDistance <= 0) return 0;

      const progressed = Math.min(Math.max(-rect.top, 0), scrollDistance);
      return progressed / scrollDistance;
    }

    update() {
      const progress = this.getProgress();
      this.section.style.setProperty('--smh-progress', progress.toFixed(4));

      if (!this.activeVideo) return;

      if (this.reducedMotionQuery.matches && this.reducedMotionMode === 'poster') {
        return;
      }

      if (this.activeVideo.readyState < 1 || !this.activeVideo.duration || Number.isNaN(this.activeVideo.duration)) {
        return;
      }

      if (this.reducedMotionQuery.matches && this.reducedMotionMode === 'first_frame') {
        if (Math.abs(this.activeVideo.currentTime) > 0.01) {
          this.activeVideo.currentTime = 0;
        }
        this.section.classList.add('is-video-ready');
        return;
      }

      const maxTime = Math.max(this.activeVideo.duration - 0.033, 0);
      const targetTime = progress * maxTime;

      if (Math.abs(targetTime - this.lastTargetTime) < 0.01) return;

      this.lastTargetTime = targetTime;

      if (Math.abs(this.activeVideo.currentTime - targetTime) > 0.01) {
        try {
          this.activeVideo.currentTime = targetTime;
          this.section.classList.add('is-video-ready');
        } catch (error) {}
      }
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);

      if (this.mql.removeEventListener) {
        this.mql.removeEventListener('change', this.onMediaChange);
      } else if (this.mql.removeListener) {
        this.mql.removeListener(this.onMediaChange);
      }

      if (this.reducedMotionQuery.removeEventListener) {
        this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
      } else if (this.reducedMotionQuery.removeListener) {
        this.reducedMotionQuery.removeListener(this.onReducedMotionChange);
      }

      [this.desktopVideo, this.mobileVideo].forEach((video) => {
        if (!video) return;
        video.removeEventListener('loadedmetadata', this.onLoadedMetadata);
      });

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
      }
    }
  }

  function init(root = document) {
    const sections = root.matches?.(SECTION_SELECTOR)
      ? [root]
      : root.querySelectorAll?.(SECTION_SELECTOR);

    if (!sections) return;

    sections.forEach((section) => {
      if (instances.has(section)) return;
      const instance = new ScrollMediaHero(section);
      instances.set(section, instance);
      instance.init();
    });
  }

  function destroy(root = document) {
    const sections = root.matches?.(SECTION_SELECTOR)
      ? [root]
      : root.querySelectorAll?.(SECTION_SELECTOR);

    if (!sections) return;

    sections.forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;
      instance.destroy();
      instances.delete(section);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init(document));

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });
})();